// ── Design decisions ──
// Panel lateral derecho para "ver/editar sin perder el contexto de la lista".
// Esc cierra (listener en document mientras está abierto), el foco se mueve al
// panel al abrir y vuelve al elemento que lo disparó al cerrar. El fondo es un
// botón invisible para que el clic afuera también cierre. Sin portal: el
// contenedor ya está a nivel de página y así se mantiene trivial en jsdom.
//
// `ambiance` es SÓLO piel: pinta el chrome con los tokens de ambiance.css y
// abre el scope `.amb-ext` para los hijos. Lo pide únicamente el
// ExtractionDrawer; el resto de los llamadores (Manifiesto, Nueva Entrada,
// Recepción Digital, Clientes) lo omite y sigue viendo el panel de siempre.

import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

const ANCHOS = { md: 'max-w-md', lg: 'max-w-2xl', xl: 'max-w-4xl' };

const Drawer = ({ open, title, onClose, children, width = 'md', footer = null, ambiance = false }) => {
  const panelRef = useRef(null);
  const previoRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    previoRef.current = document.activeElement;
    panelRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose && onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const prev = previoRef.current;
      if (prev && typeof prev.focus === 'function') prev.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className={`fixed inset-0 z-40 flex justify-end${ambiance ? ' amb-ext' : ''}`}>
      <button
        type="button"
        aria-label="Cerrar panel (fondo)"
        tabIndex={-1}
        onClick={onClose}
        className={`absolute inset-0 ${ambiance ? 'amb-scrim' : 'bg-slate-900/30'}`}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={`relative flex h-full w-full ${ANCHOS[width] || ANCHOS.md} flex-col outline-none ${
          ambiance ? 'amb-panel' : 'bg-white shadow-xl'
        }`}
      >
        <div
          className={
            ambiance
              ? 'amb-panel-head'
              : 'flex items-center justify-between border-b border-slate-200 px-4 py-3'
          }
        >
          <h2 className={ambiance ? 'amb-panel-title' : 'text-sm font-semibold text-slate-900'}>{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar panel"
            title="Cerrar panel (Esc)"
            className={
              ambiance
                ? 'amb-ibtn amb-ibtn--bare'
                : 'rounded-md p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700'
            }
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <div
          className={`flex-1 overflow-y-auto ${
            ambiance ? 'amb-panel-body amb-scroll' : 'bg-[#f8fafc] px-4 py-3'
          }`}
        >
          {children}
        </div>
        {footer ? (
          <div className={ambiance ? 'amb-panel-foot' : 'border-t border-slate-200 bg-white px-4 py-3'}>
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Drawer;
